import React, { useState } from "react";
import { Box, Typography, Slider, Button, Divider } from "@mui/material";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { useNavigate } from "react-router-dom";

import { useAppTheme } from "../../theme/useAppTheme";
import useCurrency from "../../hooks/useCurrency";

ChartJS.register(ArcElement, Tooltip, Legend);

const styles = `
.value-chip-green {
  background: #e6fff3;
  padding: 6px 18px;
  border-radius: 8px;
  color: #00a565;
  font-weight: 700;
  font-size: 16px;
  margin-left: 10px;
}

.value-chip-blue {
  background: #e8ecff;
  padding: 6px 18px;
  border-radius: 8px;
  color: #6a1b9a;
  font-weight: 700;
  font-size: 16px;
  margin-left: 10px;
}

.label-title {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 6px;
}
`;

export default function SipCalculator() {
  const navigate = useNavigate();
  const [investment, setInvestment] = useState(25000);
  const [growth, setGrowth] = useState(12);
  const [years, setYears] = useState(10);

  const themeColors = useAppTheme();

  const { symbol } = useCurrency();

  const months = years * 12;
  const monthlyRate = Math.pow(1 + growth / 100, 1 / 12) - 1;

  const totalValue = Math.round(
    investment *
      ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate) *
      (1 + monthlyRate),
  );

  const investedAmount = investment * months;
  const returns = totalValue - investedAmount;

  const chartData = {
    labels: ["Invested amount", "Est. returns"],
    datasets: [
      {
        data: [investedAmount, returns],
        backgroundColor: ["#dde2ff", "#6a1b9a"],
        borderWidth: 0,
      },
    ],
  };

  const chartOptions = {
    cutout: "50%",
    plugins: { legend: { position: "right" } },
  };

  return (
    <Box
      sx={{
        background: themeColors.scaffoldGradientColors,
        minHeight: "100vh",
        px: 3,
        py: 5,
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        overflowY: "auto",
      }}
    >
      <style>{styles}</style>

      <Box sx={{ width: "100%", maxWidth: "1200px" }}>
        <Typography
          variant="h4"
          fontWeight="bold"
          align="center"
          sx={{
            mb: 2,
            fontSize: { xs: "1.8rem", sm: "2.2rem" },
            background: themeColors.primary,
            backgroundClip: "text",
            textFillColor: "transparent",
          }}
        >
          SIP Calculator
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Box
          sx={{
            background: themeColors.bigContainer,
            border: `1px solid ${themeColors.scaffold}`,
            color: themeColors.text,
            boxShadow: "0px 8px 25px rgba(0,0,0,0.06)",
            borderRadius: "12px",
            p: { xs: 2, md: 5 },
            display: "flex",
            gap: { xs: 3, md: 6 },
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          {/* LEFT SECTION */}
          <Box sx={{ flex: 1 }}>
            <Typography className="label-title">Monthly investment</Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <span className="value-chip-green">{symbol} {investment}</span>
            </Box>

            <Slider
              value={investment}
              onChange={(e, v) => setInvestment(v)}
              min={100}
              max={1000000}
              sx={{ mt: 1, color: "#00a565" }}
            />

            <Typography className="label-title" sx={{ mt: 3 }}>
              Expected return rate (p.a)
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <span className="value-chip-blue">{growth}%</span>
            </Box>

            <Slider
              value={growth}
              onChange={(e, v) => setGrowth(v)}
              min={1}
              max={30}
              sx={{ mt: 1, color: "#6a1b9a" }}
            />
            <Typography className="label-title" sx={{ mt: 3 }}>
              Time period
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center" }}>
              <span className="value-chip-blue">{years} Yr</span>
            </Box>

            <Slider
              value={years}
              onChange={(e, v) => setYears(v)}
              min={1}
              max={40}
              sx={{ mt: 1, color: "#6a1b9a" }}
            />
            <Box sx={{ mt: 4, fontSize: 15 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <span>Invested amount</span>
                <strong>{symbol} {investedAmount.toLocaleString()}</strong>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <span>Est. returns</span>
                <strong>{symbol} {returns.toLocaleString()}</strong>
              </Box>
            </Box>
          </Box>

          {/* RIGHT SECTION */}
          <Box sx={{ flex: 1, textAlign: "center" }}>
            <Box sx={{ width: "100%", maxWidth: 340, mx: "auto" }}>
              <Doughnut data={chartData} options={chartOptions} />
            </Box>

            <Typography sx={{ mt: 3, fontSize: 24, fontWeight: 700 }}>
              Total Value: {symbol}{totalValue.toLocaleString()}
            </Typography>

            <Button
              variant="contained"
              sx={{
                mt: 3,
                background: themeColors.primary,
                color: themeColors.text,
                "&:hover": {
                  background: themeColors.svgColor,
                },
                border: `1px solid ${themeColors.scaffold}`,
                px: 4,
                py: 1.4,
                fontSize: 16,
                borderRadius: "8px",
                textTransform: "none",
              }}
              onClick={() => navigate("/AllSipScreen")}
            >
              + Start New SIP
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
